import React from 'react';
import { ExternalLink, Clock, Rocket } from 'lucide-react';

const RecentLaunches = ({ launches }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;

    if (diffMs < 0) return 'Just now';

    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return '<1m ago';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHrs < 24) return `${diffHrs}h ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const truncateAddress = (url) => {
    const match = String(url || '').match(/0x[a-fA-F0-9]+/);
    if (match) {
      const addr = match[0];
      return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    }
    return 'View contract';
  };

  const latest = Array.isArray(launches) && launches.length > 0 ? launches[0] : null;
  const rest = Array.isArray(launches) && launches.length > 1 ? launches.slice(1) : [];

  const TokenImage = ({ src, alt, size = 56 }) => {
    const s = `${size}px`;
    if (src) {
      return (
        <img
          src={src}
          alt={alt}
          style={{ width: s, height: s }}
          className="rounded-xl object-cover border border-white/10 bg-white/5"
        />
      );
    }
    const initials = String(alt || 'T')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((x) => x[0].toUpperCase())
      .join('');

    return (
      <div
        style={{ width: s, height: s }}
        className="rounded-xl border border-white/10 bg-gradient-to-br from-[#ff3b3b]/30 via-white/5 to-white/0 flex items-center justify-center"
      >
        <span className="text-white/70 font-display font-bold">{initials || 'T'}</span>
      </div>
    );
  };

  return (
    <section id="launches" className="py-20 px-6 relative z-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-[#ff3b3b]/20">
            <Rocket className="w-5 h-5 text-[#ff3b3b]" />
          </div>
          <h2 className="text-3xl font-display font-bold text-white">Recent Launches</h2>
        </div>
        <p className="text-white/50 mb-8">Latest deployed tokens (Base)</p>

        {latest ? (
          <div className="space-y-4">
            {/* Featured / Latest */}
            <div className="glass-card p-6 md:p-7 border border-[#ff3b3b]/25">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <TokenImage src={latest.image_url} alt={latest.name} size={72} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] bg-[#ff3b3b]/20 text-[#ff6b6b] border border-[#ff3b3b]/30">
                        Latest
                      </span>
                      <div className="flex items-center gap-1 text-white/40 bg-white/5 px-2 py-1 rounded-lg">
                        <Clock className="w-3 h-3" />
                        <span className="text-xs">{formatDate(latest.timestamp)}</span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-display font-bold text-white truncate mt-2">
                      {latest.name}
                    </h3>
                    <p className="text-[#ff3b3b] font-semibold">${latest.ticker}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {latest.contract_url ? (
                    <a
                      href={latest.contract_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-white/5 text-white/70 text-sm rounded-lg hover:bg-[#ff3b3b]/20 hover:text-[#ff3b3b] transition-all"
                      title={latest.contract_url}
                    >
                      <ExternalLink className="w-3 h-3" />
                      {truncateAddress(latest.contract_url)}
                    </a>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Compact list */}
            {rest.length > 0 ? (
              <div className="glass-card p-4">
                <div className="divide-y divide-white/5">
                  {rest.map((launch, index) => (
                    <div key={index} className="py-3 first:pt-0 last:pb-0 flex items-center gap-3">
                      <TokenImage src={launch.image_url} alt={launch.name} size={44} />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-white font-semibold truncate">{launch.name}</p>
                            <p className="text-white/50 text-sm">${launch.ticker}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/40 text-xs whitespace-nowrap">
                              {formatDate(launch.timestamp)}
                            </span>
                            {launch.contract_url ? (
                              <a
                                href={launch.contract_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-white/60 hover:text-[#ff3b3b] transition-colors whitespace-nowrap"
                                title={launch.contract_url}
                              >
                                {truncateAddress(launch.contract_url)}
                              </a>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="glass-card p-12 text-center">
            <p className="text-white/50">No launches revealed yet</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default RecentLaunches;
